/**
 * Hook para obtener los bloques de tiempo personalizados
 *
 * Calcula los bloques horarios basándose en wakeTime y sleepTime
 * de las preferencias del usuario.
 */

import { useMemo } from 'react';
import { useUIStore } from '@/store';
import {
  calculateTimeBlocks,
  groupProtocolsByTimeBlock,
  getCurrentTimeBlock,
  getActiveTimeBlock,
  type TimeBlockConfig,
} from '@/core/constants';
import type { TimeBlock, Protocol } from '@/core/types';

/**
 * Hook que retorna los bloques de tiempo calculados según las preferencias del usuario
 */
export function useTimeBlocks() {
  const preferences = useUIStore((state) => state.preferences);

  const timeBlocks = useMemo(() => {
    return calculateTimeBlocks(preferences.wakeTime, preferences.sleepTime);
  }, [preferences.wakeTime, preferences.sleepTime]);

  return timeBlocks;
}

/**
 * Hook que retorna la configuración de un bloque específico
 */
export function useTimeBlockConfig(timeBlock: TimeBlock): TimeBlockConfig {
  const timeBlocks = useTimeBlocks();
  return timeBlocks[timeBlock];
}

/**
 * Hook que retorna el bloque horario actual según los horarios del usuario
 */
export function useCurrentTimeBlock(): TimeBlock {
  const timeBlocks = useTimeBlocks();
  return getCurrentTimeBlock(timeBlocks);
}

/**
 * Hook que retorna los protocolos agrupados por bloque horario
 */
export function useGroupedProtocols(protocols: Protocol[]) {
  const timeBlocks = useTimeBlocks();

  return useMemo(() => {
    return groupProtocolsByTimeBlock(protocols, timeBlocks);
  }, [protocols, timeBlocks]);
}

/**
 * Hook que retorna el bloque activo (actual o siguiente con tareas pendientes)
 */
export function useActiveTimeBlock(protocols: Protocol[]): TimeBlock {
  const timeBlocks = useTimeBlocks();
  return getActiveTimeBlock(protocols, timeBlocks);
}
